import { Disclosure, Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useWebsocket } from 'contexts/ActionCableContext';
import { useBrand } from 'contexts/BrandContext';
import { HiringHeroSmallBanner } from 'features/HiringHeroSmallBanner';
import { HiringHeroSuccessModal } from 'features/HiringHeroSuccessModal';
import { useEmployer } from 'hooks/redux/useEmployer';
import { useEmployerProfileSystemNotifications } from 'hooks/redux/useEmployerProfileSystemNotifications';
import { useHiringHeroMetric } from 'hooks/redux/useHiringHeroMetric';
import { HiringHeroIcon } from 'icons/complex';
import { DotIcon } from 'icons/solid';
import { find } from 'lodash';
import filter from 'lodash/filter';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import uniqBy from 'lodash/uniqBy';
import Head from 'next/head';
import Image from 'next/legacy/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import schoolImage from 'public/images/defaults/school_image.png';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import {
  fetchEmployerProfileNotifications,
  hiringHeroMetricsLoadSuccess,
} from 'redux/employer/actions';
import { isImpersonated, signOut } from 'util/api/auth';
import { HiringHeroMetricResource } from 'util/resources/HiringHeroMetric';
import { AppRoutes, EarlyDayRoutes, employerUrl } from 'util/urlFactory';
import { EmployerHiringUpdateFlow } from '../../features/EmployerHiringUpdateFlow';
import { EmployerHiringUpdateFlowType } from '../../features/EmployerHiringUpdateFlow/EmployerHiringUpdateFlow.constants';
import { useFeatureFlag } from '../../hooks/redux/useFeatureFlag';
import { useEmployerJobApplications } from '../../hooks/useEmployerJobApplications';
import { useEmployerJobListings } from '../../hooks/useEmployerJobListings';
import { UserProfile } from '../../util/resources';
import { NotificationType } from '../../util/resources/SystemNotification';
import LoggedOutLayout from './loggedOut';

const navigation = [
  { name: 'Search Educators', href: AppRoutes.SEARCH_EDUCATORS, current: false },
  { name: 'My Jobs', href: AppRoutes.EMPLOYER_JOBS, current: false },
  { name: 'Applicants', href: AppRoutes.EMPLOYER_APPLICANTS, current: false },
  { name: 'Messages', href: AppRoutes.EMPLOYER_MESSAGES, current: false },
  { name: 'Interviews', href: AppRoutes.EMPLOYER_INTERVIEWS, current: false },
  { name: 'My Account', href: AppRoutes.EMPLOYER_PROFILE, current: false },
];

const navigationSection2 = [
  { name: 'Help & FAQ', href: EarlyDayRoutes.HELP },
  { name: 'Contact Us', href: EarlyDayRoutes.CONTACT_US },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const EmployerLayout = (props) => {
  const {
    loaded,
    currentAccount,
    currentEmployerProfile = {},
    title,
    showHiringHeroBanner,
  } = props;

  const router = useRouter();
  const { isHiringHero, enabled, loaded: metricsLoaded } = useHiringHeroMetric();

  const { employer } = useEmployer();

  const { jobApplications, loading: jobApplicationsLoading } = useEmployerJobApplications({
    initialQueryProps: {
      includes: ['user_profile'],
      // TODO: current limitation! Can't get the user profiles BY ID, so we have to try and get them throught he applications
    },
  });
  const { jobs: jobListings } = useEmployerJobListings({
    includes: ['locations'],
  });

  const jobApplicationsPrimitive = map(jobApplications, 'id').join(',');
  const jobListingsPrimitive = map(jobListings, 'id').join(',');

  const applicants = useMemo(
    () =>
      uniqBy(
        map(jobApplications, (jobApplication) => jobApplication.userProfile()),
        'id',
      ),
    [jobApplicationsPrimitive],
  );

  const locations = useMemo(
    () =>
      uniqBy(
        reduce(jobListings, (acc, jobListing) => [...acc, ...jobListing.locations().toArray()], []),
        'id',
      ),
    [jobListingsPrimitive],
  );

  const dispatch = useDispatch();

  const [src, setSrc] = useState(employer?.profileImage?.downloadUrl || schoolImage);
  const [renderHiringHeroBanner, setRenderHiringHeroBanner] = useState(false);

  if (loaded && !currentEmployerProfile.id) {
    return <LoggedOutLayout>{props.children}</LoggedOutLayout>;
  }

  const { firstName, lastName } = currentEmployerProfile;
  const name = firstName + ' ' + lastName;
  const unreadInterviewCount = currentAccount?.meta?.unreadInterviewsNotificationsCount;

  const userNavigation = [
    {
      name: 'Switch Account',
      href: AppRoutes.EMPLOYER_PROFILE_CHANGE,
      requireMultiEmployerProfile: true,
    },
    { name: 'Profile Preview', href: employer?.slug ? employerUrl(employer) : '#' },
    { name: 'Settings & Privacy', href: AppRoutes.EMPLOYER_PROFILE_PREFERENCES },
  ];

  const hiringHeroBannerConditions =
    currentEmployerProfile.id && showHiringHeroBanner && !isHiringHero && metricsLoaded && enabled;

  const { systemNotifications } = useEmployerProfileSystemNotifications();
  const systemNotificationsPrimitive = JSON.stringify(systemNotifications);

  const userProfilesPendingHiringUpdateIds = useMemo(() => {
    const profilesPending = filter(
      systemNotifications,
      (notification) =>
        notification?.notificationType === NotificationType.USER_PROFILE_REPORTED_HIRE,
    );

    if (!profilesPending) return [];

    return reduce(
      profilesPending,
      (userProfileIds, notification) => {
        return [
          ...userProfileIds,
          ...map(
            filter(notification.metadata, (metadata) => isUndefined(metadata?.status)),
            'userProfileId',
          ),
        ];
      },
      [],
    );
  }, [systemNotificationsPrimitive]);

  const userProfilesPendingHiringUpdateIdsPrimitive = userProfilesPendingHiringUpdateIds.join(',');

  const [userProfilesPendingHiringUpdate, setUserProfilesPendingHiringUpdate] = useState([]);

  useEffect(() => {
    if (!userProfilesPendingHiringUpdateIds.length) {
      return;
    }
    void UserProfile.where({
      userProfileId: userProfilesPendingHiringUpdateIds,
    })
      .all()
      .then((userProfiles) => {
        setUserProfilesPendingHiringUpdate(userProfiles.toArray());
      });
  }, [userProfilesPendingHiringUpdateIdsPrimitive]);

  const [showOnGoingHireReportFlow, setShowOnGoingHireReportFlow] = useState(false);

  const activatedOnGoingHireReportFlowFlag = useFeatureFlag(
    'hiring_update_on_long_gap_between_surveys',
  );

  useEffect(() => {
    setShowOnGoingHireReportFlow(
      !isImpersonated() &&
        Boolean(
          find(
            systemNotifications,
            (notification) => notification.notificationType === NotificationType.SHOW_HIRING_SURVEY,
          ),
        ) &&
        activatedOnGoingHireReportFlowFlag &&
        !jobApplicationsLoading,
    );
  }, [systemNotificationsPrimitive, activatedOnGoingHireReportFlowFlag, jobApplicationsLoading]);

  const currentUserProfilePendingHiringUpdate = useMemo(() => {
    if (!currentEmployerProfile?.id || !userProfilesPendingHiringUpdate?.[0]?.id) {
      return null;
    }

    return userProfilesPendingHiringUpdate[0];
  }, [userProfilesPendingHiringUpdate?.[0]?.id, currentEmployerProfile?.id]);

  const [showTeacherReportFlow, setShowTeacherReportFlow] = useState(false);

  const activatedTeacherReportFlowFlag = useFeatureFlag(
    'hiring_update_on_survey_reported_by_teacher',
  );

  useEffect(() => {
    setShowTeacherReportFlow(
      !isImpersonated() &&
        Boolean(currentUserProfilePendingHiringUpdate?.id) &&
        activatedTeacherReportFlowFlag &&
        !jobApplicationsLoading,
    );
  }, [
    currentUserProfilePendingHiringUpdate?.id,
    activatedTeacherReportFlowFlag,
    jobApplicationsLoading,
  ]);

  useEffect(() => {
    if (hiringHeroBannerConditions) {
      const hiringHeroClicked = localStorage.getItem('hiring_hero_banner_clicked');
      !hiringHeroClicked && setRenderHiringHeroBanner(true);
    } else {
      setRenderHiringHeroBanner(false);
    }
  }, [hiringHeroBannerConditions]);

  useWebsocket('EmployerChannel', { employer_id: employer?.id }, (websocket) => {
    switch (websocket.type) {
      case 'hiring_hero_metric_update':
        const interfaceObj = HiringHeroMetricResource.interface();
        const data = websocket.data.map((metric) => interfaceObj.buildResource(metric, [], {}));
        dispatch(hiringHeroMetricsLoadSuccess(data));
        break;
      default:
        break;
    }
  });

  useEffect(() => {
    if (currentEmployerProfile?.id) {
      dispatch(fetchEmployerProfileNotifications());
    }
  }, [currentEmployerProfile?.id]);

  const { brand } = useBrand();

  return (
    <>
      <Head>
        <title>{brand?.name}</title>
      </Head>
      <HiringHeroSuccessModal />
      <div className="flex flex-col min-h-screen bg-white">
        <Disclosure
          as="nav"
          className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-nav"
        >
          {({ open }) => (
            <>
              <div className="px-4">
                <div className="mx-auto max-w-7xl">
                  <div className="relative flex items-center justify-between h-16 gap-6">
                    <div className="flex items-center flex-1 gap-6">
                      <div className="flex-shrink-0 w-32 -ml-1.5">
                        {brand?.logo && (
                          <Image
                            priority
                            width={108}
                            height={16}
                            layout="responsive"
                            src={brand.logo?.downloadUrl}
                            alt={`${brand.name} Logo`}
                          />
                        )}
                      </div>

                      {/* Links section */}
                      <div className="flex-1 hidden lg:block">
                        <div className="flex items-center justify-center gap-4">
                          {navigation.map((item) => (
                            <Link
                              key={item.name}
                              href={item.href}
                              aria-current={item.current ? 'page' : undefined}
                            >
                              <span
                                className={classNames(
                                  item.href == router.pathname
                                    ? 'bg-gray-100'
                                    : 'hover:text-gray-700',
                                  'px-3 py-2 rounded-md text-sm font-medium text-gray-900 cursor-pointer',
                                )}
                                data-cy={`LargeEmployerMenuItem${item.name}`}
                              >
                                {item.name}
                                {item.name === 'Messages' && !!props.unreadMessageCount && (
                                  <span className="bg-primary-pure-blue ml-3 inline-block py-0.5 px-3 text-xs font-medium rounded-full text-white">
                                    {props.unreadMessageCount}
                                  </span>
                                )}

                                {item.name === 'Applicants' &&
                                  !!props.unreviewedApplicantsCount && (
                                    <span className="bg-primary-pure-blue ml-3 inline-block py-0.5 px-3 text-xs font-medium rounded-full text-white">
                                      {props.unreviewedApplicantsCount}
                                    </span>
                                  )}

                                {item.name === 'Interviews' && !!unreadInterviewCount && (
                                  <DotIcon className="h-2 w-2 text-primary-poppy-red inline-block ml-0.5" />
                                )}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex lg:hidden">
                      {/* Mobile menu button */}
                      <Disclosure.Button
                        className="inline-flex items-center justify-center p-2 text-gray-400 rounded-md bg-gray-50 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-purple-500"
                        data-cy="OpenEmployerMenuButton"
                      >
                        <span className="sr-only">Open main menu</span>
                        {open ? (
                          <XMarkIcon className="block w-6 h-6" aria-hidden="true" />
                        ) : (
                          <Bars3Icon className="block w-6 h-6" aria-hidden="true" />
                        )}
                      </Disclosure.Button>
                    </div>

                    <div className="hidden lg:block">
                      <div className="flex items-center gap-5">
                        {isHiringHero && enabled && (
                          <Link
                            className="flex items-center gap-3 px-5 border-x border-neutral"
                            href={AppRoutes.EMPLOYER_HIRING_HERO_STATS_PAGE}
                          >
                            <HiringHeroIcon />
                            <p className="text-sm text-neutral-ink-blue">Hiring Hero</p>
                          </Link>
                        )}
                        {/* Profile dropdown */}
                        <Menu as="div" className="relative flex-shrink-0">
                          <div>
                            <Menu.Button className="flex text-sm text-white text-gray-500 rounded-full bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-purple-500">
                              <span className="sr-only">Open user menu</span>
                              {/* <img className="w-8 h-8 rounded-full" src={user.imageUrl} alt="" /> */}
                              <>
                                <div className={`h-8 w-8 relative block overflow-hidden`}>
                                  <Image
                                    src={src}
                                    className={`rounded-full`}
                                    placeholder="blur"
                                    blurDataURL={schoolImage}
                                    onError={() => setSrc(schoolImage)}
                                    // blurDataURL={`data:image/svg+xml;base64,${toBase64(shimmer(700, 475))}`}
                                    layout="fill"
                                    objectFit="cover"
                                    priority
                                    alt="user image"
                                  />
                                </div>
                                <ChevronDownIcon
                                  className="self-center w-5 h-5"
                                  aria-hidden="true"
                                />
                              </>
                            </Menu.Button>
                          </div>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute right-0 z-40 w-48 mt-2 overflow-hidden origin-top-right bg-white divide-y rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              <div>
                                <Menu.Item key={name}>
                                  {({ active }) => (
                                    <a
                                      href={AppRoutes.EMPLOYER_PROFILE}
                                      className={classNames(
                                        active ? 'bg-gray-100' : '',
                                        'block px-4 py-3 text-sm text-gray-700 bg-white',
                                        !name ? 'hidden sm:hidden' : '',
                                      )}
                                    >
                                      <p className="font-bold">{name}</p>
                                      <p className="text-gray-500">{employer?.name}</p>
                                    </a>
                                  )}
                                </Menu.Item>
                              </div>
                              <div className="py-2 bg-white">
                                {userNavigation.map((item) => {
                                  if (
                                    item.requireMultiEmployerProfile &&
                                    currentAccount?.employerProfiles?.length <= 1
                                  ) {
                                    return null;
                                  }
                                  return (
                                    <Menu.Item key={item.name}>
                                      {({ active }) => (
                                        <a
                                          href={item.href}
                                          className={clsx(
                                            'block px-4 py-2 text-sm text-gray-700 cursor-pointer',
                                            {
                                              'bg-gray-100': active,
                                            },
                                          )}
                                        >
                                          {item.name}
                                        </a>
                                      )}
                                    </Menu.Item>
                                  );
                                })}
                              </div>
                              <div className="py-2 bg-white">
                                {navigationSection2.map((item) => (
                                  <Menu.Item key={item.name}>
                                    {({ active }) => (
                                      <a
                                        target="_blank"
                                        href={item.href}
                                        className={classNames(
                                          active ? 'bg-gray-100' : '',
                                          'block px-4 py-2 text-sm text-gray-700 bg-white',
                                        )}
                                        rel="noreferrer"
                                      >
                                        {item.name}
                                      </a>
                                    )}
                                  </Menu.Item>
                                ))}
                              </div>
                              <div>
                                <Menu.Item key={'signout'}>
                                  {({ active }) => (
                                    <a
                                      className={classNames(
                                        active ? 'bg-gray-100' : '',
                                        'block px-4 py-3 text-sm text-gray-700 cursor-pointer',
                                      )}
                                      onClick={signOut}
                                    >
                                      Sign Out
                                    </a>
                                  )}
                                </Menu.Item>
                              </div>
                            </Menu.Items>
                          </Transition>
                        </Menu>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Disclosure.Panel className="absolute left-0 z-40 w-full border-b border-gray-200 bg-gray-50 lg:hidden top-full">
                <div className="flex items-center px-5 py-4">
                  <div className="flex-shrink-0">
                    <div className={`h-8 w-8 relative block overflow-hidden`}>
                      <Image
                        src={src}
                        className={`rounded-full`}
                        placeholder="blur"
                        blurDataURL={schoolImage}
                        onError={() => setSrc(schoolImage)}
                        // blurDataURL={`data:image/svg+xml;base64,${toBase64(shimmer(700, 475))}`}
                        layout="fill"
                        objectFit="cover"
                        priority
                        alt="user image"
                      />
                    </div>
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-800">{name}</div>
                    <div className="text-sm font-medium text-gray-500">{employer?.name}</div>
                  </div>
                </div>
                <div className="border-t border-gray-200 ">
                  <div className="p-2 pb-3 space-y-1">
                    {navigation.map((item) => (
                      <Link href={item.href} key={item.name}>
                        <span
                          className={classNames(
                            item.current ? 'bg-gray-100' : 'hover:text-gray-700',
                            'block px-3 py-2 cursor-pointer rounded-md text-sm font-medium text-gray-900',
                          )}
                          data-cy={`SmallEmployerMenuItem${item.name}`}
                        >
                          {item.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                  <div className="p-2 space-y-1 border-t">
                    {userNavigation.map((item, index) => (
                      <a
                        key={index}
                        href={item.href}
                        className={classNames(
                          'block p-3 py-2 text-sm font-medium text-gray-900 rounded-md cursor-pointer hover:bg-gray-100',
                        )}
                      >
                        <p>{item.name}</p>
                      </a>
                    ))}
                  </div>
                  <div className="p-2 space-y-1 border-t">
                    {navigationSection2.map((item) => (
                      <Fragment key={item.href}>
                        <a
                          target="_blank"
                          href={item.href}
                          className={classNames(
                            'block p-3 py-2 text-sm font-medium text-gray-900 rounded-md cursor-pointer hover:bg-gray-100',
                          )}
                          rel="noreferrer"
                        >
                          {item.name}
                        </a>
                      </Fragment>
                    ))}
                  </div>
                  <div className="px-2 border-t">
                    <a
                      className={classNames(
                        'block px-3 py-4 text-sm font-medium text-gray-900 rounded-md cursor-pointer hover:bg-gray-100',
                      )}
                      onClick={signOut}
                    >
                      Sign Out
                    </a>
                  </div>
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
        {renderHiringHeroBanner && (
          <HiringHeroSmallBanner
            onClose={() => {
              setRenderHiringHeroBanner(false);
            }}
            onClick={() => {
              localStorage.setItem('hiring_hero_banner_clicked', new Date().toString());
              router.push(AppRoutes.HIRING_HEROS_LANDING_PAGE);
            }}
          />
        )}
        {title && (
          <div className={clsx('w-full px-4 pb-4', renderHiringHeroBanner ? 'pt-6' : 'pt-8')}>
            <h1 className="md:text-[32px] text-[20px] text-neutral-ink-blue font-bold mx-auto max-w-7xl">
              {title}
            </h1>
          </div>
        )}
        <main className="flex-1 w-full px-4 justify-stretch">
          <div className="mx-auto max-w-7xl">{props.children}</div>
        </main>
      </div>

      {currentUserProfilePendingHiringUpdate?.id && showTeacherReportFlow ? (
        <EmployerHiringUpdateFlow
          flowType={EmployerHiringUpdateFlowType.ON_REPORTED_BY_TEACHER}
          userProfile={currentUserProfilePendingHiringUpdate}
          applicants={applicants}
          employerLocations={locations}
          jobListings={jobListings}
          open={showTeacherReportFlow}
          onClose={() => {
            setShowTeacherReportFlow(false);
            dispatch(fetchEmployerProfileNotifications());
          }}
        />
      ) : (
        <EmployerHiringUpdateFlow
          flowType={EmployerHiringUpdateFlowType.ON_LONG_GAP_BETWEEN_SURVEYS}
          open={showOnGoingHireReportFlow}
          applicants={applicants}
          employerLocations={locations}
          jobListings={jobListings}
          onClose={() => {
            setShowOnGoingHireReportFlow(false);
            dispatch(fetchEmployerProfileNotifications());
          }}
        />
      )}
    </>
  );
};

function mapStateToProps(state) {
  return {
    loaded: state.account.loaded,
    unreadMessageCount: state.conversations.unreadCount,
    unreviewedApplicantsCount: state.applications.unreviewedApplicants.count,
    currentAccount: state.account,
    currentEmployerProfile: state.employer.currentEmployerProfile,
  };
}

export default connect(mapStateToProps)(EmployerLayout);
